import { defineStore } from "pinia";
import { ref } from "vue";
import ticketCategoryService from "@/services/hrm/ticketCategoryService";
import { toast } from "@/utils/toast";
import { createScopedCache } from "./_cache";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

const categoryListCache = createScopedCache("tickets:categories:list");
const categoryStatsCache = createScopedCache("tickets:categories:stats");

const CATEGORY_LIST_TTL = 5 * 60 * 1000;
const CATEGORY_STATS_TTL = 60 * 1000;

export const useTicketCategoryStore = defineStore("ticketCategory", () => {
  const items = ref([]);
  const stats = ref([]);
  const loading = ref(false);
  const error = ref(null);

  async function fetchAll(options = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_TICKETS))) {
      items.value = [];
      return { items: [] };
    }

    loading.value = true;
    error.value = null;
    try {
      return await categoryListCache.get({}, {
        ttlMs: CATEGORY_LIST_TTL,
        force: options.force === true,
        loader: async () => {
          const res = await ticketCategoryService.getAll();
          return { items: res.data?.data || res.data || [] };
        },
        onHydrate: ({ items: rows }) => {
          items.value = rows || [];
        },
      });
    } catch (e) {
      error.value = e.response?.data?.message || "Failed to fetch categories";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function ensureAll(options = {}) {
    return await fetchAll({ ...options, force: false });
  }

  async function create(payload) {
    loading.value = true;
    error.value = null;
    try {
      const res = await ticketCategoryService.create(payload);
      invalidateCategoryCaches();
      toast("Category created");
      await fetchAll({ force: true });
      return res.data;
    } catch (e) {
      error.value = e.response?.data?.message || "Failed to create category";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    error.value = null;
    try {
      const res = await ticketCategoryService.update(id, payload);
      invalidateCategoryCaches();
      toast("Category updated");
      await fetchAll({ force: true });
      return res.data;
    } catch (e) {
      error.value = e.response?.data?.message || "Failed to update category";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loading.value = true;
    error.value = null;
    try {
      await ticketCategoryService.delete(id);
      invalidateCategoryCaches();
      toast("Category deleted");
      await fetchAll({ force: true });
    } catch (e) {
      error.value = e.response?.data?.message || "Failed to delete category";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function findIdByName(name) {
    if (!name) return null;
    const hit = items.value.find((c) => c.name === name);
    return hit?.id ?? null;
  }

  async function fetchStats(params = {}, options = {}) {
    loading.value = true;
    error.value = null;
    try {
      return await categoryStatsCache.get(params, {
        ttlMs: CATEGORY_STATS_TTL,
        force: options.force === true,
        loader: async () => {
          const res = await ticketCategoryService.stats(params);
          return { stats: res.data?.data || res.data || [] };
        },
        onHydrate: ({ stats: rows }) => {
          stats.value = rows || [];
        },
      });
    } catch (e) {
      error.value =
        e.response?.data?.message || "Failed to fetch category stats";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function ensureStats(params = {}, options = {}) {
    return await fetchStats(params, { ...options, force: false });
  }

  function invalidateCategoryCaches() {
    categoryListCache.clear();
    categoryStatsCache.clear();
  }

  return {
    items,
    stats,
    loading,
    error,
    fetchAll,
    ensureAll,
    fetchStats,
    ensureStats,
    create,
    update,
    remove,
    findIdByName,
    invalidateCategoryCaches,
  };
});
